import {LoadingContextType} from "../../../context/loadingContext/LoadingContext";

/**
 * A utility function that wraps the execution of a given function, handling the loading state
 * by showing and hiding the loading indicator before and after the function execution.
 *
 * @param func The function to execute, which can be a synchronous or asynchronous function.
 * @param loadingContext An object containing the methods to show and hide the loading indicator.
 * @param options An optional object containing various options for the withLoading function:
 *   - showConsoleLogs (boolean): Optional flag to show console logs. Defaults to false.
 *   - isMounted ({ current: boolean }): Optional object to track if the component is still mounted before hiding the loading indicator.
 *   - source (string): Optional name of the function being executed. Used for logging and as a source identifier.
 * @returns The result of the executed function or a rejected promise in case of an error.
 */
export const withLoading = async (
  func: (options: { isMounted?: { current: boolean } }) => any,
  {
    showLoading,
    hideLoading,
    showConsoleLogs: contextShowConsoleLogs,
  }: LoadingContextType, // Destructure showConsoleLogs from the context
  options: {
    showConsoleLogs?: boolean;
    isMounted?: { current: boolean };
    source?: string; // Add the source variable here
  } = {}
) => {
  const {
    showConsoleLogs = contextShowConsoleLogs, // Use the contextShowConsoleLogs as the default value
    isMounted = { current: true },
    source,
  } = options;
  try {
    showLoading(source, showConsoleLogs); // Pass the source to showLoading

    // Log the start time
    const startTime = new Date().getTime();

    // Pass the options object to the function
    const result = await Promise.resolve(func({ isMounted }));

    // Calculate and log the load time
    const endTime = new Date().getTime();
    const loadTime = endTime - startTime;
    if (showConsoleLogs) {
      const functionName = source
        ? `[withLoading] Function: ${source} | `
        : '[withLoading] ';
      console.log(`${functionName}Load time: ${loadTime} ms`);
    }

    return result;
  } finally {
    if (isMounted.current) {
      // Check if the component is still mounted before hiding the loading
      hideLoading(source, showConsoleLogs); // Pass the source to hideLoading
    }
  }
};
